/// <amd-module name="Test/TestCaseResults/Component/TestCaseResultsMainComponent" />
import * as React from "react";
import * as Reselect from "Reselect";
import { ResultsTableConnectedComponent } from "Test/TestCaseResults/Component/ResultsTableComponent";
import * as Constants from "Test/TestCaseResults/Constants";
import { TestCaseResultsModule } from "Test/TestCaseResults/Redux/TestCaseResultsModule";
import * as Resources from "Test/TestCaseResults/Resources";
import { getDataProviderContentForTestResults } from "Test/TestCaseResults/Utils";
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { ObservableValue } from "VSS/Core/Observable";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Uri } from "VSS/Platform/Util/Url";
import { IContentViewObserverModuleArgs, IFilterArgs } from "VSS/Redux/Modules/ContentViewObserver/ContentViewObserverContracts";
import { IRootProviderProps, RootProvider } from "VSS/Redux/RootProvider";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { ConnectedHeaderCommandBar } from "VSSUI/ReduxHeaderCommandBar";
import { Tooltip } from "VSSUI/TooltipEx";
import { Filter, FilterContext } from "VSSUI/Utilities/Filter";
import { ZeroData } from "VSSUI/ZeroData";

export interface ITestCaseResultsSagaContext {
    pageContext: IVssPageContext;
}

/**
 * TestCaseResultsMainComponent: The root component which renders the entire test results view.
 */
export class TestCaseResultsMainComponent extends VssComponent {
    private _rootProviderProps: IRootProviderProps<ITestCaseResultsSagaContext, any>;
    private _data: Contracts.TestCaseResultsData;
    private _filterArgs: IFilterArgs | undefined;

    constructor(props: never, context: IVssComponentContext) {
        super(props, context);

        this._data = getDataProviderContentForTestResults(context.pageContext);
        this._rootProviderProps = this._getRootProviderProps();
    }

    public render(): JSX.Element {
        let view: JSX.Element = <></> as JSX.Element;
        let header: JSX.Element = <></> as JSX.Element;

        if (!this._filterArgs) {
            this._filterArgs = {
                filter: new Filter(),
                filterToggled: new ObservableValue<boolean>(false)
            };
        }

        const contentViewObserverModuleArgs: IContentViewObserverModuleArgs = {
            filterArgs: this._filterArgs
        };

        if (!this._data || !this._data.testCaseName) {
            return (
                <div className="testcase-results-component-container">
                    <RootProvider {...this._rootProviderProps} contentViewObserverModuleArgs={contentViewObserverModuleArgs}>
                        {this._getNoTestCaseComponent()}
                    </RootProvider>
                </div>
            );
        } else {
            const resultsPresent = this._data.results && this._data.results.length > 0;
            header = this._getHeader(resultsPresent);
            if (!resultsPresent) {
                view = this._getZeroData();
            } else {
                view = <ResultsTableConnectedComponent />;
            }
        }
        return (
            <div className="testcase-results-component-container">
                <RootProvider {...this._rootProviderProps} contentViewObserverModuleArgs={contentViewObserverModuleArgs}>
                    <FilterContext.Provider value={this._filterArgs}>
                        <div className="testcase-results-component flex flex-column flex-grow">
                            {header}
                            <div className="testcase-results-view flex justify-center flex-grow flex-column">{view}</div>
                        </div>
                    </FilterContext.Provider>
                </RootProvider>
            </div>
        );
    }

    private _getZeroData(): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, Constants.ZeroDataImagePath);
        return (
            <ZeroData
                imagePath={image}
                className="testcase-results-zero-data"
                imageAltText={Resources.NoResultsFound}
                primaryText={Resources.NoResultsFound}
                secondaryText={Resources.NoResultsFoundSecondary}
            />
        );
    }

    private _getNoTestCaseComponent(): JSX.Element {
        // This is returned when the test case of that id does not exist.
        const image = getAssetLocation(this.context.pageContext, Constants.NoTestCaseImagePath);
        return (
            <ZeroData
                imagePath={image}
                className="testcase-results-no-points"
                imageAltText={Resources.NoPointsFound}
                primaryText={Resources.NoPointsFound}
                secondaryText={Resources.NoPointsFoundSecondary}
            />
        );
    }

    private _getHeader(includeFilterButton: boolean): JSX.Element {
        return (
            <div className="testcase-results-header-bar flex">
                <Tooltip overflowOnly={true} text={this._data.testCaseName}>
                    <div className="testcase-results-header text-ellipsis title-m">
                        <span>{Utils_String.format(Resources.ExecutionHistoryHeader, this._data.testCaseName)}</span>
                    </div>
                </Tooltip>
                {this._getFilterButton(includeFilterButton)}
            </div>
        );
    }

    private _getFilterButton(includeFilterButton: boolean): JSX.Element {
        return (
            <div className="testcase-results-header-command-bar flex-grow">
                <ConnectedHeaderCommandBar showFilter={includeFilterButton} commandsSelector={CommandSelector} viewActionsSelector={ViewActions} />
            </div>
        );
    }

    private _getRootProviderProps(): IRootProviderProps<ITestCaseResultsSagaContext, any> {
        const uri = new Uri(window.location.href);

        const modules = [TestCaseResultsModule];

        return {
            initialState: {},
            sagaContext: { pageContext: this.context.pageContext },
            scenarioName: "TestCaseResults",
            modules: modules
        };
    }
}

/**
 * We do not have any view actions for now, we need to render only the filter button
 *
 */
export function ViewActions(): IHeaderCommandBarItem[] {
    const tabBarCommands: IHeaderCommandBarItem[] = [];
    return tabBarCommands;
}

export const CommandSelector = Reselect.createSelector(
    // Just a placeholder for now; the ConnectedHeaderCommandBar requires this but we don't have any commands
    [() => {}],
    () => {
        return [];
    }
);

VssComponent.register("TestCaseResultsMainComponent", TestCaseResultsMainComponent);
